import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import { Badge, IconButton, Popover } from '@mui/material';
import { http } from '../../core/http/client';
import type { NotificationRow } from '../../core/api/types';
import { useAppSelector } from '../../app/store';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Delete from '@mui/icons-material/Delete';
import Close from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import './notifications.css';

export function NotificationBell() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { activeOrganizationId, profile } = useAppSelector(state => state.session);
  const personal = !activeOrganizationId || pathname === '/me' || pathname.startsWith('/me/');
  const context = `${profile.id}:${activeOrganizationId}:${personal}`;
  const current = useRef(context);
  current.current = context;
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [error, setError] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  useEffect(() => {
    let active = true;
    setItems([]); setAnchor(null); setError(false); setPending(null);
    const load = async () => {
      try {
        const { data } = await http().get<NotificationRow[]>('/notifications/', { params: { personal, pageSize: 100 }, skipErrorToast: true });
        if (active) { setItems(data); setError(false); }
      } catch { if (active) setError(true); }
    };
    void load();
    const timer = window.setInterval(() => { void load(); }, 15000);
    const onFocus = () => { void load(); };
    window.addEventListener('focus', onFocus);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [context, personal]);
  const [hidden, setHidden] = useState<string[]>([]);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { setHidden([]); setExpanded(false); }, [context]);
  const visible = items.filter(item => !hidden.includes(item.id));
  const relativeTime = (value: string) => {
    const minutes = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60000));
    if (minutes < 1) return 'Now';
    if (minutes < 60) return minutes + ' min';
    if (minutes < 1440) return Math.floor(minutes / 60) + ' hr';
    return Math.floor(minutes / 1440) + ' d';
  };
  const markRead = async (rows: typeof items) => {
    setPending('reading');
    try {
      for (const item of rows.filter(row => !row.readAt)) {
        await http().put('/notifications/' + item.id + '/read', undefined, { params: { personal } });
        if (current.current === context) setItems(previous => previous.map(row => row.id === item.id ? { ...row, readAt: new Date().toISOString() } : row));
      }
    } catch { if (current.current === context) setError(true); }
    finally { if (current.current === context) setPending(null); }
  };
  const notificationContent = (<section aria-label={t('hub.inbox.title', 'Notifications')}>
<header><strong>{t('hub.inbox.title', 'Notifications')}</strong><button type="button" className="notice-close" aria-label={t('hub.inbox.close', 'Close')} onClick={() => { setExpanded(false); setAnchor(null); }}><Close fontSize="small" /></button></header>
 <div className="notice-actions">
 {visible.some(item => !item.readAt) && <button type="button" className="notice-mark" disabled={pending !== null} onClick={() => void markRead(visible)}><CheckCircle fontSize="small" />{t('hub.inbox.markAllRead', 'Mark all as read')}</button>}
 {visible.length > 0 && <button type="button" className="notice-clear" onClick={() => setHidden(items.map(item => item.id))}><Delete fontSize="small" />{t('hub.inbox.clearPanel', 'Clear panel')}</button>}
 </div>
 {error && <p role="alert">{t('hub.inbox.error', 'Notifications could not be loaded.')}</p>}
 {!error && !visible.length && <p>{t('hub.inbox.empty', 'No notifications yet.')}</p>}
 <div className="platform-notifications-list">{(expanded ? items : visible.slice(0, 4)).map(item => <button type="button" className="notice-row" key={item.id} data-unread={!item.readAt} disabled={pending !== null} aria-label={!item.readAt ? t('hub.inbox.markRead', 'Mark as read') : undefined} onClick={() => void markRead([item])}>
 <span className="notice-avatar" aria-hidden="true">N</span><span className="notice-content"><span className="notice-message">{item.title}</span><span className="notice-message">{item.message}</span><time dateTime={item.createdAt} title={new Date(item.createdAt).toLocaleString()}>{relativeTime(item.createdAt)}</time></span>{!item.readAt && <span className="notice-dot" aria-hidden="true" />}
 </button>)}</div>
 {visible.length > 0 && !expanded && <footer><button type="button" onClick={() => { setHidden([]); setExpanded(true); }}>{t('hub.inbox.viewAll', 'View all notifications')}</button></footer>}
      </section>);
  return <>
    <IconButton aria-label={t('hub.inbox.title', 'Notifications')} onClick={e => setAnchor(e.currentTarget)} aria-expanded={Boolean(anchor)}>
      <Badge badgeContent={visible.filter(item => !item.readAt).length} color="error" max={99}><NotificationsNoneOutlined /></Badge>
    </IconButton>
    <Popover open={Boolean(anchor) && !expanded} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ className: 'platform-notifications-panel' }} BackdropProps={{ sx: { backdropFilter: 'none', backgroundColor: 'transparent' } }}>
      {notificationContent}
    </Popover>
    <Dialog open={Boolean(anchor) && expanded} onClose={() => { setExpanded(false); setAnchor(null); }} fullWidth maxWidth="md" PaperProps={{ className: 'platform-notifications-panel notifications-full-view' }}>{notificationContent}</Dialog>
  </>;
}
